/**
 * Media Player Component
 * Renders image, audio, or video with display configuration
 */

import * as React from 'react';
import { cn } from '@ai-content-hub/content-kit';
import { resolveMediaUrlForViewer, shouldResolveMediaUrlForViewer } from '@ai-content-hub/content-core';
import { Loader2 } from 'lucide-react';
import { useElementUITranslations } from '../i18n/elementUI';
import type { MediaElementData, MediaDisplayConfig } from './schema';
import {
    DEFAULT_MEDIA_DISPLAY_CONFIG,
    getDisplayStyles,
    getAspectRatioStyles,
} from './schema';

export interface MediaPlayerProps {
    /** Media data */
    data: MediaElementData;
    /** Override display configuration (merges with data.displayConfig) */
    displayConfig?: MediaDisplayConfig;
    /** Additional class names */
    className?: string;
    /** Whether to show controls (for audio/video) */
    showControls?: boolean;
    /** Autoplay (for audio/video) */
    autoplay?: boolean;
    /** Loop (for audio/video) */
    loop?: boolean;
    /** Muted (for video) */
    muted?: boolean;
}

export const MediaPlayer: React.FC<MediaPlayerProps> = ({
    data,
    displayConfig,
    className,
    showControls = true,
    autoplay = false,
    loop = false,
    muted = false,
}) => {
    const et = useElementUITranslations();
    const src = (data.src ?? '').trim();
    const [resolvedSrc, setResolvedSrc] = React.useState<string | null>(() =>
        src && !shouldResolveMediaUrlForViewer(src) ? src : null,
    );
    const [resolving, setResolving] = React.useState(() =>
        Boolean(src && shouldResolveMediaUrlForViewer(src)),
    );

    React.useEffect(() => {
        const s = (data.src ?? '').trim();
        if (!s || typeof window === 'undefined') {
            setResolvedSrc(null);
            setResolving(false);
            return;
        }
        if (!shouldResolveMediaUrlForViewer(s)) {
            setResolvedSrc(s);
            setResolving(false);
            return;
        }
        let cancelled = false;
        setResolving(true);
        setResolvedSrc(null);
        void (async () => {
            try {
                const uri = await resolveMediaUrlForViewer(s);
                if (!cancelled) {
                    setResolvedSrc(uri || s);
                }
            } catch {
                if (!cancelled) {
                    setResolvedSrc(s);
                }
            } finally {
                if (!cancelled) {
                    setResolving(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [data.src]);

    const { mediaType, title } = data;

    const displaySrc = resolvedSrc ?? '';
    const showProtectedLoading =
        Boolean(src && shouldResolveMediaUrlForViewer(src)) && (resolving || !resolvedSrc);

    // Pause + release audio/video on unmount so playback stops when the
    // learner navigates away (TC011: audio continues after nav).
    const audioRef = React.useRef<HTMLAudioElement | null>(null);
    const videoRef = React.useRef<HTMLVideoElement | null>(null);
    React.useEffect(() => {
        return () => {
            for (const el of [audioRef.current, videoRef.current]) {
                if (el) {
                    el.pause();
                    el.removeAttribute('src');
                    el.load();
                }
            }
        };
    }, []);

    // Merge display configs: props override data.displayConfig override defaults
    const mergedConfig: MediaDisplayConfig = {
        ...DEFAULT_MEDIA_DISPLAY_CONFIG,
        ...data.displayConfig,
        ...displayConfig,
    };

    // Get styles
    const mediaStyles = getDisplayStyles(mergedConfig);
    const containerStyles = getAspectRatioStyles(mergedConfig);

    // No source - show placeholder
    if (!src) {
        return (
            <div
                className={cn(
                    'media-player media-player--empty',
                    'flex items-center justify-center',
                    'rounded-lg border-2 border-dashed border-muted-foreground/25',
                    'bg-muted/30 text-muted-foreground',
                    'min-h-[100px]',
                    className
                )}
                style={containerStyles}
            >
                <span className="text-sm">{et.noMediaSelected.replace('{mediaType}', mediaType)}</span>
            </div>
        );
    }

    // Render based on media type
    const renderMedia = () => {
        if (showProtectedLoading) {
            return (
                <div
                    className={cn(
                        'media-player__loading flex items-center justify-center gap-2',
                        'min-h-[100px] text-sm text-muted-foreground'
                    )}
                >
                    <Loader2 className="h-6 w-6 animate-spin shrink-0" />
                    <span>{et.loadingMedia}</span>
                </div>
            );
        }

        switch (mediaType) {
            case 'image':
                return (
                    <img
                        src={displaySrc}
                        alt={title || ''}
                        style={mediaStyles}
                        className={cn(
                            'media-player__image',
                            mergedConfig.crop && 'object-cover'
                        )}
                        loading="lazy"
                    />
                );

            case 'audio':
                return (
                    <audio
                        ref={audioRef}
                        src={displaySrc}
                        controls={showControls}
                        autoPlay={autoplay}
                        loop={loop}
                        className="media-player__audio w-full"
                        style={{
                            maxWidth: mediaStyles.maxWidth,
                        }}
                    >
                        <track kind="captions" />
                        Your browser does not support the audio element.
                    </audio>
                );

            case 'video':
                return (
                    <video
                        ref={videoRef}
                        src={displaySrc}
                        controls={showControls}
                        autoPlay={autoplay}
                        loop={loop}
                        muted={muted}
                        style={mediaStyles}
                        className="media-player__video"
                    >
                        <track kind="captions" />
                        Your browser does not support the video element.
                    </video>
                );

            default:
                return null;
        }
    };

    // If aspect ratio is set, wrap in container
    if (containerStyles) {
        return (
            <div
                className={cn(
                    'media-player',
                    `media-player--${mediaType}`,
                    'relative overflow-hidden rounded',
                    className
                )}
                style={{
                    ...containerStyles,
                    backgroundColor: mergedConfig.backgroundColor,
                    borderRadius: mediaStyles.borderRadius,
                }}
            >
                <div className="absolute inset-0 flex items-center justify-center">
                    {renderMedia()}
                </div>
            </div>
        );
    }

    // No aspect ratio - render directly
    return (
        <div
            className={cn(
                'media-player',
                `media-player--${mediaType}`,
                className
            )}
            style={{
                backgroundColor: mergedConfig.backgroundColor,
            }}
        >
            {renderMedia()}
        </div>
    );
};

export default MediaPlayer;

